import { PlatformLocation } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';

import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil, takeWhile, tap } from 'rxjs/operators';

import { ModalsActions } from 'src/app/features/modals';
import { PackageUser } from 'src/app/features/package-users';

import { PinValidationActions, PinValidationSelectors } from '../../store';
import { selectPinVerificationViewModel } from '../../store/selectors/pin-validation.selectors';

@Component({
    selector: 'app-pin-verification',
    templateUrl: './pin-verification.component.html',
    styleUrls: ['./pin-verification.component.scss'],
    standalone: false
})
export class PinVerificationComponent implements OnInit, OnDestroy {
  @Input() onPinVerified: () => void;
  @Input() participant: PackageUser;

  readonly vm$ = this.store.select(selectPinVerificationViewModel);
  readonly pinVerification$ = this.store.select(PinValidationSelectors.selectPinVerification);

  pinEntry: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
  pinValidationForm: UntypedFormGroup;
  onDestroyNotifier = new Subject();

  constructor(public location: PlatformLocation, private readonly store: Store) {
    location.onPopState(() => {
      this.store.dispatch(ModalsActions.ClearModalComponent());
    });
  }

  ngOnInit(): void {
    this.store.dispatch(PinValidationActions.PinVerificationOpened());

    this.pinValidationForm = new UntypedFormGroup({
      pinEntry: this.pinEntry,
    });

    combineLatest([this.vm$, this.pinVerification$])
      .pipe(
        takeUntil(this.onDestroyNotifier),
        filter(([_, pinVerification]) => !!pinVerification),
        tap(([_, pinVerification]) => {
          if (pinVerification.isPinValid) {
            this.onPinVerified();
          }
        }),
        takeWhile(([_, pinVerification]) => !pinVerification.isPinValid),
        tap(([viewModel, _]) => {
          this.pinEntry.setErrors([{ invalid: viewModel.message }]);
          this.pinEntry.setValue('');
        })
      )
      .subscribe();
  }

  verifyPin(): void {
    this.pinEntry.markAsDirty();
    const pin = this.pinEntry.value;

    if (!pin) {
      this.store.dispatch(PinValidationActions.EmptyPinInput());
      return;
    }

    this.store.dispatch(
      PinValidationActions.VerifyPin({
        payload: {
          packageUserGuid: this.participant.packageUserGuid,
          pin: pin,
        },
      })
    );
  }

  verifyPinOnEnterKey(event: KeyboardEvent) {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      this.verifyPin();
    }
  }

  cancel() {
    this.store.dispatch(ModalsActions.ClearModalComponent());
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.next(undefined);
    this.onDestroyNotifier.complete();
  }
}
